import { useState } from "react";
import { Flex } from "antd";
import { colors } from "shared/theme/color";
import { translate } from "shared/languages";
import { formatCurrency } from "shared/utils";
import {
  VBOX_W,
  VBOX_H,
  PAD_LEFT,
  PAD_RIGHT,
  PAD_TOP,
  PLOT_H,
  formatShort,
  niceMax,
  bandWidth,
  bandXFor,
  yFor,
} from "./chartUtils";
import { textPx } from "shared/constants/zoom";

interface RevenueCompositionChartProps {
  data: { month: string; month_label: string; paket_revenue: number; membership_revenue: number }[];
}

const Legend = ({ color, label }: { color: string; label: string }) => (
  <Flex align="center" gap={6}>
    <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
    <span style={{ fontSize: textPx(12), color: colors.ink2 }}>{label}</span>
  </Flex>
);

const TIP_H = 68;
// Rough px-per-char at a given font size (SVG has no text-measurement API
// without a DOM round-trip) — used to size the tooltip box to its actual
// content instead of a fixed width that clips longer Rupiah amounts.
const estimateTextWidth = (text: string, fontSize: number) => text.length * fontSize * 0.62;

// Stacked bars: Paket (navy) + Membership (amber) per month, so the
// "Penjualan" total and its split are readable at a glance. A stacked bar is
// used instead of a stacked area/line — months are discrete buckets, not a
// continuous series, and an area chart draws a smooth (misleading) slope
// between points where nothing actually happened, plus its peak/valley
// shape reads as a "landscape" rather than a month-by-month comparison,
// which is harder to parse at a glance than a bar's simple height.
const RevenueCompositionChart = ({ data }: RevenueCompositionChartProps) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const isEmpty = data.every((d) => d.paket_revenue === 0 && d.membership_revenue === 0);
  const max = niceMax(
    Math.max(...data.map((d) => d.paket_revenue + d.membership_revenue), 1),
  );
  const baselineY = PAD_TOP + PLOT_H;
  const n = data.length;
  // Band scale, not xFor's point scale — a bar centered on an edge point
  // overflows the plot and covers the y-axis labels (see chartUtils).
  const band = bandWidth(n);
  const barWidth = Math.min(36, Math.max(10, band * 0.5));

  const bars = data.map((d, i) => {
    const x = bandXFor(i, n);
    const paketTopY = yFor(d.paket_revenue, max);
    const totalTopY = yFor(d.paket_revenue + d.membership_revenue, max);
    return { ...d, x, paketTopY, totalTopY };
  });

  const hovered = hoverIndex !== null ? bars[hoverIndex] : null;
  const paketLabel = translate("analyticsPage.tab.package");
  const membershipLabel = translate("analyticsPage.tab.membership");
  const line1 = hovered ? `${paketLabel}: ${formatCurrency(hovered.paket_revenue)}` : "";
  const line2 = hovered ? `${membershipLabel}: ${formatCurrency(hovered.membership_revenue)}` : "";
  // Left offset (dot + gap) + right padding, so the widest line never
  // touches the box edge.
  const tipW = hovered
    ? Math.max(150, Math.ceil(Math.max(estimateTextWidth(line1, 12), estimateTextWidth(line2, 12))) + 40)
    : 0;
  const tipLeft = hovered
    ? Math.min(Math.max(hovered.x - tipW / 2, PAD_LEFT), VBOX_W - PAD_RIGHT - tipW)
    : 0;
  const tipTop = hovered
    ? Math.max(Math.min(hovered.paketTopY, hovered.totalTopY) - TIP_H - 10, PAD_TOP)
    : 0;

  return (
    <div>
      <Flex gap={20} style={{ marginBottom: 16 }}>
        <Legend color={colors.navy} label={translate("analyticsPage.tab.package")} />
        <Legend color={colors.amber} label={translate("analyticsPage.tab.membership")} />
      </Flex>

      {isEmpty ? (
        <Flex
          align="center"
          justify="center"
          style={{ height: VBOX_H, color: colors.ink3, fontSize: textPx(13) }}
        >
          {translate("analyticsPage.sales.trend_empty")}
        </Flex>
      ) : (
        <svg
          width="100%"
          height={VBOX_H}
          viewBox={`0 0 ${VBOX_W} ${VBOX_H}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {[0, 0.5, 1].map((f) => {
            const y = yFor(max * f, max);
            return (
              <g key={f}>
                <line
                  x1={PAD_LEFT}
                  x2={VBOX_W - 8}
                  y1={y}
                  y2={y}
                  stroke={colors.line}
                  strokeWidth={1}
                />
                <text x={PAD_LEFT - 8} y={y + 3} textAnchor="end" fontSize={10} fill={colors.ink3}>
                  {formatShort(max * f)}
                </text>
              </g>
            );
          })}

          {bars.map((b, i) => {
            const active = hoverIndex === i;
            return (
              <g key={b.month}>
                <rect
                  x={b.x - barWidth / 2}
                  y={b.paketTopY}
                  width={barWidth}
                  height={Math.max(0, baselineY - b.paketTopY)}
                  fill={colors.navy}
                  opacity={hoverIndex === null || active ? 1 : 0.45}
                />
                <rect
                  x={b.x - barWidth / 2}
                  y={b.totalTopY}
                  width={barWidth}
                  height={Math.max(0, b.paketTopY - b.totalTopY)}
                  fill={colors.amber}
                  opacity={hoverIndex === null || active ? 1 : 0.45}
                />
                <text x={b.x} y={VBOX_H - 8} textAnchor="middle" fontSize={10} fill={colors.ink3}>
                  {b.month}
                </text>
                {/* Full-band hit area, wider than the bar itself, so hovering
                    near a short bar doesn't require pixel-precise aim. */}
                <rect
                  x={b.x - band / 2}
                  y={PAD_TOP}
                  width={band}
                  height={PLOT_H}
                  fill="transparent"
                  onMouseEnter={() => setHoverIndex(i)}
                  onMouseLeave={() => setHoverIndex(null)}
                />
              </g>
            );
          })}

          {hovered && (
            <g pointerEvents="none">
              <rect
                x={tipLeft}
                y={tipTop}
                width={tipW}
                height={TIP_H}
                rx={6}
                fill={colors.ink}
              />
              <text x={tipLeft + 12} y={tipTop + 20} fontSize={12} fontWeight={600} fill={colors.card}>
                {hovered.month_label}
              </text>
              <circle cx={tipLeft + 15} cy={tipTop + 36} r={3.5} fill={colors.navy} />
              <text x={tipLeft + 24} y={tipTop + 40} fontSize={12} fill={colors.card}>
                {line1}
              </text>
              <circle cx={tipLeft + 15} cy={tipTop + 54} r={3.5} fill={colors.amber} />
              <text x={tipLeft + 24} y={tipTop + 58} fontSize={12} fill={colors.card}>
                {line2}
              </text>
            </g>
          )}
        </svg>
      )}
    </div>
  );
};

export default RevenueCompositionChart;
